import { createScenariosSimulatorApiClient } from './api-client';

/**
 * API response types
 */
export interface MakeSimulationResponse {
  success: Record<string, unknown>;
}

export interface CompleteSimulationResponse {
  success: boolean;
}

export interface OptimizedScenarioParams {
  unit_preset_projection_id_list: [string];
  is_already_at_acl: boolean;
  simulation_id: string;
  lead_id: string;
  base_simulation_scenario_projection_id: string;
}

export interface OptimizedScenarioResponse {
  data: Record<string, unknown>;
}

/**
 * Makes a new simulation request
 *
 * @param simulationPayload - The simulation payload
 * @param token - Bearer authentication token
 * @returns Promise with simulation result
 */
export async function makeSimulation(simulationPayload: Record<string, unknown>, token: string): Promise<any> {
  const apiClient = createScenariosSimulatorApiClient(token);
  const { data } = await apiClient.post<MakeSimulationResponse>('/v2/simulation/make', simulationPayload);
  return data;
}

/**
 * Completes a simulation by ID
 *
 * @param simulationId - The simulation ID to complete
 * @param token - Bearer authentication token
 * @returns Promise with completion status
 */
export async function completeSimulation(simulationId: string, token: string): Promise<any> {
  const apiClient = createScenariosSimulatorApiClient(token);
  const { data } = await apiClient.post<CompleteSimulationResponse>(`/v2/simulation/complete/${simulationId}`);
  return data;
}

export interface ResumeSimulationResponse {
  rawInputs: Record<string, unknown>;
}

/**
 * Resumes a draft simulation by ID, returning the inputs to pre-fill the calculator
 *
 * @param simulationId - The draft simulation ID to resume
 * @param token - Bearer authentication token
 * @returns Promise with raw inputs for the calculator
 */
export async function resumeSimulation(simulationId: string, token: string): Promise<ResumeSimulationResponse> {
  const apiClient = createScenariosSimulatorApiClient(token);
  const { data } = await apiClient.get<ResumeSimulationResponse>(`/v2/simulation/resume/${simulationId}`);
  return data;
}

/**
 * Creates an optimized scenario
 *
 * @param params - Optimized scenario parameters
 * @param token - Bearer authentication token
 * @returns Promise with optimized scenario data
 */
export async function createOptimizedScenario(
  params: OptimizedScenarioParams,
  token: string,
): Promise<Record<string, unknown>> {
  const apiClient = createScenariosSimulatorApiClient(token);
  const response = await apiClient.post<OptimizedScenarioResponse>('/v2/simulation/make', params);
  return response.data.data;
}
